/**
 * O modo demonstração — o app de verdade com os dados de uma pessoa inventada.
 *
 * Para quem chega da página de apresentação e quer ver o produto antes de
 * confiar a ele os próprios números. Desenho: `docs/designs/modo-demonstracao.md`.
 *
 * ── Quem decide é o ENDEREÇO, no carregamento, e mais nada ───────────────
 *
 * `/demo` liga. Não há variável de ambiente, parâmetro nem valor guardado que
 * ligue fora dali — o que significa que ninguém entra na demonstração sem
 * querer, e que ela não "gruda": abrir `/` de novo é o app de verdade.
 *
 * É uma constante de módulo de propósito. A camada de rede, o cliente de login
 * e o armazenamento são trocados ANTES de o app montar; decidir depois seria
 * decidir tarde.
 *
 * ── O que isto NÃO é ─────────────────────────────────────────────────────
 *
 * Não é uma forma de entrar no app. O servidor não sabe que a demonstração
 * existe: não há token, conta nem rota para ela. Tudo o que aparece vem de
 * `demo/dados.ts`, no navegador, e nenhuma requisição sai (ver `services/api.ts`).
 */

export const ehCaminhoDeDemo = (caminho: string): boolean =>
  caminho === '/demo' || caminho.startsWith('/demo/');

export const MODO_DEMO: boolean =
  typeof window !== 'undefined' && ehCaminhoDeDemo(window.location.pathname);

/** Quem "está logada" na demonstração. O nome já diz que é exemplo. */
export const usuarioDaDemo = {
  id: 'demonstracao',
  email: 'marina@exemplo.com',
  user_metadata: { full_name: 'Marina (exemplo)' },
};

/**
 * A sessão da demonstração. Os tokens são texto qualquer: nunca são enviados a
 * lugar nenhum, porque no modo demonstração a camada de rede não chama a rede.
 */
export const sessaoDaDemo = {
  access_token: 'demonstracao',
  token_type: 'bearer',
  expires_in: 3600,
  refresh_token: 'demonstracao',
  user: usuarioDaDemo,
} as any;

/**
 * O aviso de "nada é gravado" escuta este evento — ver `FaixaDaDemo.tsx`.
 * O `detail` diz o motivo: `'escrita'` para qualquer gravação, `'outro-lancamento'`
 * quando ela já usou o lançamento que a demonstração deixa fazer.
 */
export const EVENTO_DE_ESCRITA_NA_DEMO = 'fintracking:demo-tentou-gravar';
export type MotivoDoAviso = 'escrita' | 'outro-lancamento';

/** A faixa escuta este para dizer que o lançamento entrou na projeção. */
export const EVENTO_DE_LANCAMENTO_NA_DEMO = 'fintracking:demo-lancou';

/**
 * Quantos lançamentos a demonstração aceita por aba (28/10).
 *
 * Um. É o bastante para ela ver a projeção mudar com um número dela — a
 * promessa da página de apresentação — e pouco para a demonstração virar um
 * app sem conta. O segundo abre o convite ao cadastro, com o porquê.
 *
 * Só criar: editar e apagar continuam barrados, inclusive o que ela lançou.
 * O lançamento mora na memória de `demo/dados.ts` e some ao recarregar.
 */
export const LANCAMENTOS_DA_DEMO = 1;
export const ehLancamentoNaDemo = (metodo: string, rota: string): boolean =>
  metodo === 'post' && rota === 'transactions';

/**
 * As chamadas que NÃO são leitura por `GET` mas também não gravam nada: são
 * perguntas. Só estas a demonstração responde; qualquer outra escrita vira o
 * aviso. A lista é fechada de propósito — rota nova que grava nasce barrada.
 */
export const PERGUNTAS_DA_DEMO: readonly string[] = [
  'insights/ask',
  'transactions/query',
  // Ler a frase do campo rápido não grava: quem grava é o lançamento, que
  // tem a sua própria trava (`LANCAMENTOS_DA_DEMO`).
  'quick-entry/parse',
];

/** Para onde vai quem sai da demonstração. */
export const SAIDA_DA_DEMO = '/conheca';

/**
 * Como as seções abrem na demonstração: a ordem, e quais ocupam a linha inteira.
 *
 * Em pares, de cima para baixo: Nova Transação (inteira) · Métodos ao lado de
 * Categorias · Investimentos ao lado de Dívidas · Metas (inteira) · Projeções
 * (inteira) · Lembretes ao lado de Nossa conta · Outras moedas (inteira) ·
 * Transações (inteira).
 */
export const ORDEM_DA_DEMO: readonly string[] = [
  'sec-form',
  'sec-methods', 'sec-chart',
  'sec-investments', 'sec-debts',
  'sec-goals',
  'sec-projections',
  'sec-reminders', 'sec-casa',
  // Lá embaixo de propósito (13/10): interessa a quem viaja, e no alto tiraria
  // a atenção de parcelas e saldo, que é o que a demonstração vende.
  'sec-moedas',
  'sec-transactions',
];

export const LARGURAS_DA_DEMO: Readonly<Record<string, boolean>> = {
  'sec-form': true,
  'sec-methods': false, 'sec-chart': false,
  'sec-investments': false, 'sec-debts': false,
  'sec-goals': true,
  'sec-projections': true,
  'sec-reminders': false, 'sec-casa': false,
  // Inteira, para não desfazer os pares de meia largura.
  'sec-moedas': true,
  'sec-transactions': true,
};
